/**
 * La plomberie HTTP, partagée — et rien d'autre.
 *
 * Ce module ne connaît ni utilisateur, ni client, ni aucun type du domaine : il
 * sait envoyer une requête et traduire une erreur. C'est ce qui permet à
 * l'application interne et à l'espace client de s'en servir tous les deux sans
 * partager la moindre notion d'identité (ADR-0012).
 */

export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    message: string,
  ) {
    super(message);
    this.name = "ErreurApi";
  }

  get estNonAuthentifie() {
    return this.statut === 401;
  }

  get estInterdit() {
    return this.statut === 403;
  }
}

export type Options = {
  methode?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  corps?: unknown;
  signal?: AbortSignal;
};

export async function appeler<T>(chemin: string, options: Options = {}): Promise<T> {
  const { methode = "GET", corps, signal } = options;

  let reponse: Response;
  try {
    reponse = await fetch(chemin, {
      method: methode,
      // L'authentification passe par un cookie `httpOnly` : il n'y a aucun
      // jeton à stocker côté navigateur, et c'est précisément ce qu'on
      // cherchait en écartant le JWT.
      credentials: "include",
      signal,
      headers: corps ? { "Content-Type": "application/json" } : undefined,
      body: corps ? JSON.stringify(corps) : undefined,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ErreurApi(0, "Le serveur est injoignable. Vérifiez votre connexion.");
  }

  if (reponse.status === 204) return undefined as T;

  const texte = await reponse.text();
  const donnees = texte ? JSON.parse(texte) : undefined;

  if (!reponse.ok) throw new ErreurApi(reponse.status, messageDErreur(donnees, reponse.status));
  return donnees as T;
}

function messageDErreur(donnees: unknown, statut: number): string {
  if (donnees && typeof donnees === "object" && "detail" in donnees) {
    const detail = (donnees as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const premier = detail[0] as { msg?: string };
      if (premier.msg) return premier.msg;
    }
  }
  if (statut === 429) return "Trop de tentatives. Réessayez dans quelques minutes.";
  if (statut >= 500) return "Le serveur a rencontré une erreur. Réessayez plus tard.";
  return "Une erreur est survenue.";
}

/**
 * Le téléversement d'un fichier — le seul appel qui n'envoie pas du JSON.
 *
 * On ne pose PAS `Content-Type` : le navigateur doit l'écrire lui-même, parce
 * que `multipart/form-data` porte une frontière (`boundary`) qu'il est seul à
 * connaître. La forcer à la main produit un corps que le serveur ne sait pas
 * découper, et une erreur qui ne dit rien de sa cause.
 */
export async function televerser<T>(
  chemin: string,
  formulaire: FormData,
  signal?: AbortSignal,
): Promise<T> {
  let reponse: Response;
  try {
    reponse = await fetch(chemin, {
      method: "POST",
      credentials: "include",
      signal,
      body: formulaire,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ErreurApi(0, "Le serveur est injoignable. Vérifiez votre connexion.");
  }

  const texte = await reponse.text();
  const donnees = texte ? JSON.parse(texte) : undefined;
  if (!reponse.ok) throw new ErreurApi(reponse.status, messageDErreur(donnees, reponse.status));
  return donnees as T;
}
